import { BaseLogicExpression } from "./BaseLogic";
import { ExpressionValue } from "../../ExpressionValue";

import { ITestingContext } from "../../../Common/Testing/ITestingContext";
import { ExecutionData } from "../../../Common/Data/ExecutionData/ExecutionData";
import { BooleanDataDefinition } from "../../../Common/Data/Definitions/BooleanDataDefinition";
import { StateManager } from "../../../Common/StateManager";
import { ExpressionContext } from "../../ExpressionContext";
import { IBaseExpressionElement } from "../../IBaseExpressionElement";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

export enum GroupingType {
    AND = "and",
    OR = "or",
}

export class AndGroup extends BaseLogicExpression {
    readonly __type: string = "And Group";
    typeOfGroup: GroupingType = GroupingType.AND;
    elements: ExpressionValue[] = [];

    constructor() {
        super();
        this.expressionValueType = new BooleanDataDefinition();

        this.add();
    }

    getExpressionValues(): ExpressionValue[] {
        return this.elements;
    }

    getAllVariables(): string[] {
        let output: string[] = [];

        for (let f of this.getExpressionValues()) {
            output.push(...f.getAllVariables());
        }

        return output;
    }

    add() {
        let e = new ExpressionValue();

        if (this.expressionContext) {
            e.setExpressionContext(this.expressionContext, this);

            this.expressionContext.refresh();
        }

        e.expectedType = new BooleanDataDefinition();
        this.elements.push(e);
        StateManager.changed(this);
    }

    delete(index: number) {
        if (this.elements.length <= 1) {
            if (this.elements[index]) {
                this.elements[index].clear();
            } else {
                this.add();
            }
        } else {
            this.elements.splice(index, 1);
        }

        if (this.expressionContext) {
            this.expressionContext.refresh();
        }
        StateManager.changed(this);
    }

    setExpressionContext(expressionContext: ExpressionContext, owner: IBaseExpressionElement): void {
        super.setExpressionContext(expressionContext, owner);

        for (let p of this.elements) {
            p.setExpressionContext(expressionContext, this);
        }
    }

    evaluate(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        return !this.elements.find((e) => e.evaluate(data, testContext) === false);
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "typeOfGroup", this.typeOfGroup);

        let elementData: object[] = [];

        for (let e of this.elements) {
            elementData.push(e.getStorageData());
        }

        Reflect.set(data, "elements", elementData);

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        if (data === undefined) throw new Error("no data");

        this.typeOfGroup = Reflect.get(data, "typeOfGroup");

        let elementData: object[] = Reflect.get(data, "elements") as object[];

        // reset elements to empty.
        this.elements = [];

        for (let e of elementData) {
            let exp = new ExpressionValue();
            exp.setStorageData(e);
            if (exp) {
                this.elements.push(exp);
            }
        }

        if (this.elements.length === 0) {
            let exp = new ExpressionValue();
            exp.expectedType = new BooleanDataDefinition();
            this.elements.push(exp);
        }
    }
}
